import { create } from 'zustand';
import type {
  Channel,
  ChannelOverview,
  ChannelReceipt,
  ChatErrorPayload,
  CreateChannelInput,
  Message,
  MessageDeleted,
  MessageReply,
  MessageType,
  PresenceStatus,
  PresenceUpdate,
  ReactionsUpdate,
  ReceiptUpdate,
  TypingUpdate,
} from '@orbit-sync/shared-types';
import { TYPING_TTL_MS } from '@orbit-sync/shared-types';
import { api, errorMessage } from '../lib/api/index.js';
import { randomId } from '../lib/randomId.js';
import type { OrbitSocket } from '../lib/socket.js';

/**
 * Chat state: channels, per-channel transcripts, presence, and messages that
 * have been sent but not yet confirmed.
 *
 * Transcripts are stored OLDEST FIRST, the order they render in. The API returns
 * newest first (it pages backwards through history), so every page is reversed
 * on the way in - once, here, rather than in every component.
 */

const PAGE_SIZE = 40;

/** How long a sent message waits for its echo before it is shown as failed. */
const SEND_TIMEOUT_MS = 10_000;

export interface PendingMessage {
  /** Client-side id. Never reaches the server - the contract has no field for one. */
  tempId: string;
  channelId: string;
  content: string;
  /** The message this answers, quoted while it is in flight. */
  replyTo: MessageReply | null;
  sentAt: string;
  status: 'sending' | 'failed';
  /** Present when the server rejected the send. */
  error?: string;
}

export interface Transcript {
  items: Message[];
  /** Cursor for the next page of OLDER messages. `null` when the start is reached. */
  nextCursor: string | null;
  hasMore: boolean;
  status: 'idle' | 'loading' | 'ready' | 'error';
  error: string | null;
  /** True while an older page is being fetched, so the button can show progress. */
  loadingOlder: boolean;
}

type LoadStatus = 'idle' | 'loading' | 'ready' | 'error';

/**
 * What the sidebar shows for a channel without loading its transcript.
 *
 * Kept separate from `transcripts` because it must survive for channels the user
 * has never opened: a message can arrive for any channel the socket is joined to,
 * and the row needs a preview whether or not history was ever fetched.
 */
export interface ChannelSummary {
  /** Which message this previews, so an edit or delete of it can update the row. */
  messageId: string;
  content: string;
  senderId: string;
  type: MessageType;
  at: string;
}

export interface ChatState {
  channels: Channel[];
  channelsStatus: LoadStatus;
  channelsError: string | null;

  transcripts: Record<string, Transcript>;
  pending: Record<string, PendingMessage[]>;
  /** Absent means UNKNOWN, not offline - see `presenceOf`. */
  presence: Record<string, PresenceStatus>;

  /** Last message per channel, for the sidebar preview and ordering. */
  summaries: Record<string, ChannelSummary>;
  /**
   * Unread counts. Loaded from the server with the conversation list (messages
   * from others after this user's read cursor), then kept current live: a
   * message arriving where the user is not looking adds one, reading - here or
   * on another device - clears it.
   */
  unread: Record<string, number>;
  /** The open channel. Messages arriving here are read on arrival. */
  activeChannelId: string | null;
  /**
   * Other members' delivery and read cursors, per channel then per user - what
   * turns a sent message's tick into a double tick and then blue. Loaded with a
   * channel's history and kept current by `chat.receipt`.
   */
  receipts: Record<string, Record<string, ChannelReceipt>>;
  /** Who is typing, per channel: userId -> when their signal arrived. */
  typing: Record<string, Record<string, number>>;
  /** The message the composer is replying to, per channel. */
  replyingTo: Record<string, Message>;

  loadChannels: () => Promise<void>;
  /**
   * After a reconnect: reloads the list, previews and counts without a loading
   * state, and refetches transcripts that may have missed messages.
   */
  resync: () => Promise<void>;
  /** This user read a channel up to `readUpTo` - possibly on another device. */
  markReadUpTo: (channelId: string, readUpTo: string) => void;
  /** Opens a conversation. Resolves with the channel so the caller can navigate. */
  createChannel: (input: CreateChannelInput) => Promise<Channel>;
  /** Inserts or replaces a channel, keeping the list's newest-first order. */
  upsertChannel: (channel: Channel) => void;
  loadHistory: (channelId: string) => Promise<void>;
  loadOlder: (channelId: string) => Promise<void>;
  sendMessage: (input: {
    socket: OrbitSocket;
    channelId: string;
    content: string;
    replyTo?: MessageReply | null;
  }) => void;
  /** Someone is typing (`chat.typing`). Lapses on its own after `TYPING_TTL_MS`. */
  applyTyping: (update: TypingUpdate) => void;
  /** A message's reactions changed (`chat.reactions`). */
  applyReactions: (update: ReactionsUpdate) => void;
  /** Sets or clears the message the composer answers. */
  setReplyingTo: (channelId: string, message: Message | null) => void;
  receiveMessage: (message: Message, currentUserId: string | null) => void;
  /** A stored message's content changed (`chat.messageEdited`). */
  applyEdit: (message: Message) => void;
  /** A stored message is gone (`chat.messageDeleted`). */
  applyDelete: (payload: MessageDeleted) => void;
  /** Someone's cursor moved (`chat.receipt`). Never moves one backwards. */
  applyReceipt: (update: ReceiptUpdate) => void;
  /** An administrator emptied a transcript, or with `null` every transcript. */
  clearHistory: (channelId: string | null) => void;
  /** This user lost access to a channel: deleted, or removed from it. */
  removeChannel: (channelId: string) => void;
  receiveChatError: (payload: ChatErrorPayload) => void;
  applyPresence: (update: PresenceUpdate) => void;
  /** Marks a channel open (clearing its unread count), or `null` when none is. */
  setActiveChannel: (channelId: string | null) => void;
  retryFailed: (socket: OrbitSocket, channelId: string, tempId: string) => void;
  discardFailed: (channelId: string, tempId: string) => void;
  reset: () => void;
}

/**
 * Stable empty values.
 *
 * These MUST be module constants, not literals created inside a selector. A
 * selector returning a fresh `[]` or `{}` on every call gives zustand's
 * `useSyncExternalStore` a new snapshot each render, which React treats as a
 * change - producing "getSnapshot should be cached" and then an infinite
 * update loop.
 */
export const EMPTY_PENDING: readonly PendingMessage[] = [];

export const EMPTY_TRANSCRIPT: Transcript = {
  items: [],
  nextCursor: null,
  hasMore: false,
  status: 'idle',
  error: null,
  loadingOlder: false,
};

function transcriptFor(state: ChatState, channelId: string): Transcript {
  return state.transcripts[channelId] ?? EMPTY_TRANSCRIPT;
}

function summaryOf(message: Message): ChannelSummary {
  return {
    messageId: message.id,
    content: message.content,
    senderId: message.senderId,
    type: message.type,
    at: message.createdAt,
  };
}

/** Guards against a message arriving twice - once over the socket, once in a history page. */
function mergeUnique(existing: Message[], incoming: Message[]): Message[] {
  const seen = new Set(existing.map((message) => message.id));
  return [...existing, ...incoming.filter((message) => !seen.has(message.id))];
}

export const useChatStore = create<ChatState>((set, get) => ({
  channels: [],
  channelsStatus: 'idle',
  channelsError: null,
  transcripts: {},
  pending: {},
  presence: {},
  summaries: {},
  unread: {},
  activeChannelId: null,
  receipts: {},
  typing: {},
  replyingTo: {},

  async loadChannels(): Promise<void> {
    set({ channelsStatus: 'loading', channelsError: null });

    try {
      const overview = await api.channels.listChannels();
      set((state) => ({ ...applyOverview(state, overview), channelsStatus: 'ready' }));
    } catch (error) {
      set({ channelsStatus: 'error', channelsError: errorMessage(error) });
    }
  },

  async resync(): Promise<void> {
    try {
      const overview = await api.channels.listChannels();

      set((state) => {
        // Every transcript but the open one is dropped and refetched when next
        // opened; the open one reloads now, below.
        const transcripts: Record<string, Transcript> = {};
        const active = state.activeChannelId;

        if (active && state.transcripts[active]) {
          transcripts[active] = state.transcripts[active];
        }

        return { ...applyOverview(state, overview), transcripts, channelsStatus: 'ready' };
      });
    } catch {
      // Still offline or restarting; the next reconnect tries again.
      return;
    }

    const active = get().activeChannelId;

    if (active) {
      await reloadTranscript(active);
    }
  },

  markReadUpTo(channelId: string, readUpTo: string): void {
    set((state) => {
      const latest = state.summaries[channelId]?.at;

      if (!state.unread[channelId] || (latest && latest > readUpTo)) {
        return state;
      }

      return { unread: omit(state.unread, channelId) };
    });
  },

  async createChannel(input: CreateChannelInput): Promise<Channel> {
    const channel = await api.channels.createChannel(input);

    // Added here rather than by refetching the list: a direct channel that
    // already existed comes back unchanged, and the server's `chat.channel`
    // broadcast may arrive either side of this response. `upsertChannel` is
    // idempotent, so whichever wins, the sidebar ends up with exactly one row.
    get().upsertChannel(channel);

    return channel;
  },

  upsertChannel(channel: Channel): void {
    set((state) => {
      const others = state.channels.filter((item) => item.id !== channel.id);

      return {
        channels: [channel, ...others].sort((a, b) => b.createdAt.localeCompare(a.createdAt)),
      };
    });
  },

  async loadHistory(channelId: string): Promise<void> {
    const current = transcriptFor(get(), channelId);

    // Already loaded or in flight: selecting a channel again must not refetch.
    if (current.status === 'loading' || current.status === 'ready') {
      return;
    }

    set((state) => ({
      transcripts: {
        ...state.transcripts,
        [channelId]: { ...EMPTY_TRANSCRIPT, status: 'loading' },
      },
    }));

    try {
      // Receipts alongside the first page, so the ticks are right on first paint.
      // A receipt failure costs colour, not the conversation.
      const [page, receipts] = await Promise.all([
        api.channels.listMessages(channelId, { limit: PAGE_SIZE }),
        api.channels.listReceipts(channelId).catch((): ChannelReceipt[] => []),
      ]);

      set((state) => {
        // The API returns newest first, so the head of the page is the latest
        // message - the one the sidebar previews.
        const latest = page.items[0];

        return {
          transcripts: {
            ...state.transcripts,
            [channelId]: {
              // Reversed: the API pages backwards, the transcript renders forwards.
              items: [...page.items].reverse(),
              nextCursor: page.nextCursor,
              hasMore: page.hasMore,
              status: 'ready',
              error: null,
              loadingOlder: false,
            },
          },
          summaries: latest
            ? { ...state.summaries, [channelId]: summaryOf(latest) }
            : state.summaries,
          receipts: {
            ...state.receipts,
            [channelId]: mergeReceipts(state.receipts[channelId], receipts),
          },
        };
      });
    } catch (error) {
      set((state) => ({
        transcripts: {
          ...state.transcripts,
          [channelId]: {
            ...EMPTY_TRANSCRIPT,
            status: 'error',
            error: errorMessage(error),
          },
        },
      }));
    }
  },

  async loadOlder(channelId: string): Promise<void> {
    const current = transcriptFor(get(), channelId);

    if (!current.hasMore || current.loadingOlder || !current.nextCursor) {
      return;
    }

    set((state) => ({
      transcripts: {
        ...state.transcripts,
        [channelId]: { ...current, loadingOlder: true },
      },
    }));

    try {
      const page = await api.channels.listMessages(channelId, {
        limit: PAGE_SIZE,
        before: current.nextCursor,
      });

      set((state) => {
        const latest = transcriptFor(state, channelId);
        const older = [...page.items].reverse();
        const seen = new Set(latest.items.map((message) => message.id));

        return {
          transcripts: {
            ...state.transcripts,
            [channelId]: {
              ...latest,
              // Older messages go in front of what is already displayed.
              items: [...older.filter((message) => !seen.has(message.id)), ...latest.items],
              nextCursor: page.nextCursor,
              hasMore: page.hasMore,
              loadingOlder: false,
            },
          },
        };
      });
    } catch (error) {
      set((state) => ({
        transcripts: {
          ...state.transcripts,
          [channelId]: {
            ...transcriptFor(state, channelId),
            loadingOlder: false,
            error: errorMessage(error),
          },
        },
      }));
    }
  },

  sendMessage({ socket, channelId, content, replyTo = null }): void {
    const tempId = randomId();

    const optimistic: PendingMessage = {
      tempId,
      channelId,
      content,
      replyTo,
      sentAt: new Date().toISOString(),
      status: 'sending',
    };

    set((state) => ({
      pending: {
        ...state.pending,
        [channelId]: [...(state.pending[channelId] ?? []), optimistic],
      },
    }));

    // Payload shape comes straight from the contract: no sender field, because
    // the server takes identity from the authenticated socket.
    socket.emit('chat.message', {
      channelId,
      content,
      ...(replyTo ? { replyToId: replyTo.id } : {}),
    });

    // A dropped socket frame produces neither an echo nor an error. Without this
    // the message would sit greyed out for ever, looking like it might yet land.
    setTimeout(() => {
      const stillPending = (get().pending[channelId] ?? []).find(
        (entry) => entry.tempId === tempId && entry.status === 'sending',
      );

      if (stillPending) {
        set((state) => ({
          pending: {
            ...state.pending,
            [channelId]: (state.pending[channelId] ?? []).map((entry) =>
              entry.tempId === tempId
                ? { ...entry, status: 'failed' as const, error: 'No confirmation from the server' }
                : entry,
            ),
          },
        }));
      }
    }, SEND_TIMEOUT_MS);
  },

  receiveMessage(message: Message, currentUserId: string | null): void {
    set((state) => {
      const current = transcriptFor(state, message.channelId);

      // A message for a channel whose history was never opened is dropped here;
      // it will be fetched in order when that channel is first selected.
      const transcripts =
        current.status === 'idle'
          ? state.transcripts
          : {
              ...state.transcripts,
              [message.channelId]: {
                ...current,
                items: mergeUnique(current.items, [message]),
              },
            };

      // Reconciliation: the server echoes to the sender too, so our own message
      // comes back with its real id. Match on content within the channel and drop
      // the optimistic copy. The contract carries no client correlation id, so
      // this is the best available match - see the note in CLAUDE.md.
      const channelPending = state.pending[message.channelId] ?? [];
      const matchIndex =
        currentUserId && message.senderId === currentUserId
          ? channelPending.findIndex(
              (entry) => entry.status === 'sending' && entry.content === message.content,
            )
          : -1;

      const pending =
        matchIndex === -1
          ? state.pending
          : {
              ...state.pending,
              [message.channelId]: channelPending.filter((_, index) => index !== matchIndex),
            };

      // The sidebar preview updates for every channel, including ones whose
      // history has never been opened.
      const summaries = { ...state.summaries, [message.channelId]: summaryOf(message) };

      // Unread counts only what the user could not have seen: another person's
      // message, in a channel that is not on screen in a visible tab.
      const mine = currentUserId !== null && message.senderId === currentUserId;
      const seen =
        mine ||
        (message.channelId === state.activeChannelId && document.visibilityState === 'visible');

      const unread = seen
        ? state.unread
        : {
            ...state.unread,
            [message.channelId]: (state.unread[message.channelId] ?? 0) + 1,
          };

      // A message from someone ends their "typing…" at once.
      const channelTyping = state.typing[message.channelId];
      const typing = channelTyping?.[message.senderId]
        ? { ...state.typing, [message.channelId]: omit(channelTyping, message.senderId) }
        : state.typing;

      return { transcripts, pending, summaries, unread, typing };
    });
  },

  applyTyping({ channelId, userId }: TypingUpdate): void {
    const key = `${channelId}:${userId}`;
    clearTimeout(typingTimers.get(key));
    typingTimers.set(
      key,
      setTimeout(() => {
        typingTimers.delete(key);
        set((state) => {
          const channelTyping = state.typing[channelId];
          return channelTyping?.[userId]
            ? { typing: { ...state.typing, [channelId]: omit(channelTyping, userId) } }
            : state;
        });
      }, TYPING_TTL_MS),
    );

    set((state) => ({
      typing: {
        ...state.typing,
        [channelId]: { ...state.typing[channelId], [userId]: Date.now() },
      },
    }));
  },

  applyReactions({ channelId, messageId, reactions }: ReactionsUpdate): void {
    set((state) => {
      const current = state.transcripts[channelId];

      if (!current) {
        return state;
      }

      return {
        transcripts: {
          ...state.transcripts,
          [channelId]: {
            ...current,
            items: current.items.map((item) =>
              item.id === messageId ? { ...item, reactions } : item,
            ),
          },
        },
      };
    });
  },

  setReplyingTo(channelId: string, message: Message | null): void {
    set((state) => ({
      replyingTo: message
        ? { ...state.replyingTo, [channelId]: message }
        : omit(state.replyingTo, channelId),
    }));
  },

  applyEdit(message: Message): void {
    set((state) => {
      const current = state.transcripts[message.channelId];
      const summary = state.summaries[message.channelId];

      return {
        transcripts: current
          ? {
              ...state.transcripts,
              [message.channelId]: {
                ...current,
                items: current.items.map((item) => (item.id === message.id ? message : item)),
              },
            }
          : state.transcripts,
        summaries:
          summary?.messageId === message.id
            ? { ...state.summaries, [message.channelId]: summaryOf(message) }
            : state.summaries,
      };
    });
  },

  applyDelete({ channelId, messageId }: MessageDeleted): void {
    set((state) => {
      const current = state.transcripts[channelId];
      const items = current ? current.items.filter((item) => item.id !== messageId) : null;
      const summary = state.summaries[channelId];

      let summaries = state.summaries;

      if (summary?.messageId === messageId) {
        // The preview was the message that went. The newest one left takes its
        // place if the transcript is loaded; otherwise there is nothing honest
        // to show until it is.
        const latest = items?.at(-1);
        summaries = latest
          ? { ...state.summaries, [channelId]: summaryOf(latest) }
          : omit(state.summaries, channelId);
      }

      return {
        transcripts:
          current && items
            ? { ...state.transcripts, [channelId]: { ...current, items } }
            : state.transcripts,
        summaries,
      };
    });
  },

  applyReceipt(update: ReceiptUpdate): void {
    set((state) => ({
      receipts: {
        ...state.receipts,
        [update.channelId]: mergeReceipts(state.receipts[update.channelId], [update]),
      },
    }));
  },

  clearHistory(channelId: string | null): void {
    set((state) => {
      const cleared = (id: string): boolean => channelId === null || id === channelId;

      const transcripts: Record<string, Transcript> = {};

      for (const [id, transcript] of Object.entries(state.transcripts)) {
        transcripts[id] = cleared(id)
          ? { ...transcript, items: [], nextCursor: null, hasMore: false }
          : transcript;
      }

      return {
        transcripts,
        summaries: keepWhere(state.summaries, (id) => !cleared(id)),
        unread: keepWhere(state.unread, (id) => !cleared(id)),
      };
    });
  },

  removeChannel(channelId: string): void {
    set((state) => ({
      channels: state.channels.filter((channel) => channel.id !== channelId),
      transcripts: omit(state.transcripts, channelId),
      pending: omit(state.pending, channelId),
      summaries: omit(state.summaries, channelId),
      unread: omit(state.unread, channelId),
      receipts: omit(state.receipts, channelId),
      typing: omit(state.typing, channelId),
      replyingTo: omit(state.replyingTo, channelId),
    }));
  },

  receiveChatError(payload: ChatErrorPayload): void {
    const channelId = payload.channelId;

    if (!channelId) {
      return;
    }

    set((state) => {
      const channelPending = state.pending[channelId] ?? [];
      // The contract carries no correlation id, so the oldest unconfirmed send in
      // that channel is the one this rejection belongs to.
      const index = channelPending.findIndex((entry) => entry.status === 'sending');

      if (index === -1) {
        return state;
      }

      return {
        pending: {
          ...state.pending,
          [channelId]: channelPending.map((entry, position) =>
            position === index
              ? { ...entry, status: 'failed' as const, error: payload.message }
              : entry,
          ),
        },
      };
    });
  },

  applyPresence(update: PresenceUpdate): void {
    set((state) => ({
      presence: { ...state.presence, [update.userId]: update.status },
    }));
  },

  setActiveChannel(channelId: string | null): void {
    set((state) => {
      if (state.activeChannelId === channelId && (channelId === null || !state.unread[channelId])) {
        // Nothing to change. Returning the same state keeps this safe to call
        // from an effect that runs on every render of the chat page.
        return state;
      }

      if (channelId === null) {
        return { activeChannelId: null };
      }

      const { [channelId]: _read, ...rest } = state.unread;

      return { activeChannelId: channelId, unread: rest };
    });
  },

  retryFailed(socket: OrbitSocket, channelId: string, tempId: string): void {
    const entry = (get().pending[channelId] ?? []).find((item) => item.tempId === tempId);

    if (!entry) {
      return;
    }

    get().discardFailed(channelId, tempId);
    get().sendMessage({ socket, channelId, content: entry.content, replyTo: entry.replyTo });
  },

  discardFailed(channelId: string, tempId: string): void {
    set((state) => ({
      pending: {
        ...state.pending,
        [channelId]: (state.pending[channelId] ?? []).filter((entry) => entry.tempId !== tempId),
      },
    }));
  },

  reset(): void {
    set({
      channels: [],
      channelsStatus: 'idle',
      channelsError: null,
      transcripts: {},
      pending: {},
      presence: {},
      summaries: {},
      unread: {},
      activeChannelId: null,
      receipts: {},
      typing: {},
      replyingTo: {},
    });

    for (const timer of typingTimers.values()) {
      clearTimeout(timer);
    }

    typingTimers.clear();
  },
}));

/**
 * Folds the server's conversation list into the store: the channels, their
 * previews and their unread counts. The open conversation counts as read - it
 * is on screen - and a preview newer than the server's (a message that arrived
 * over the socket while the request was in flight) is kept.
 */
function applyOverview(
  state: ChatState,
  overview: ChannelOverview[],
): Pick<ChatState, 'channels' | 'summaries' | 'unread'> {
  const summaries: Record<string, ChannelSummary> = {};
  const unread: Record<string, number> = {};

  for (const item of overview) {
    const local = state.summaries[item.id];
    const remote = item.lastMessage
      ? {
          messageId: item.lastMessage.id,
          content: item.lastMessage.content,
          senderId: item.lastMessage.senderId,
          type: item.lastMessage.type,
          at: item.lastMessage.createdAt,
        }
      : undefined;
    const newest = local && (!remote || local.at > remote.at) ? local : remote;

    if (newest) {
      summaries[item.id] = newest;
    }

    if (item.unreadCount > 0 && item.id !== state.activeChannelId) {
      unread[item.id] = item.unreadCount;
    }
  }

  const channels = overview
    .map(({ lastMessage: _last, unreadCount: _unread, ...channel }) => channel)
    .sort((a, b) => b.createdAt.localeCompare(a.createdAt));

  return { channels, summaries, unread };
}

/** Refetches one transcript from the newest page, replacing what is held. */
async function reloadTranscript(channelId: string): Promise<void> {
  useChatStore.setState((state) => ({
    transcripts: omit(state.transcripts, channelId),
  }));
  await useChatStore.getState().loadHistory(channelId);
}

/** Expiry timers for typing signals, outside the store: they are not state. */
const typingTimers = new Map<string, ReturnType<typeof setTimeout>>();

/** Stable empty typing map, for a selector with nobody typing. */
export const EMPTY_TYPING: Record<string, number> = {};

/** A copy of `record` without `key`. */
function omit<T>(record: Record<string, T>, key: string): Record<string, T> {
  const { [key]: _omitted, ...rest } = record;
  return rest;
}

/** A copy of `record` with only the keys `keep` accepts. */
function keepWhere<T>(
  record: Record<string, T>,
  keep: (key: string) => boolean,
): Record<string, T> {
  return Object.fromEntries(Object.entries(record).filter(([key]) => keep(key)));
}

/** The later of two cursor positions. ISO-8601 UTC strings compare correctly as text. */
function later(a: string | null | undefined, b: string | null | undefined): string | null {
  if (!a) {
    return b ?? null;
  }

  if (!b) {
    return a;
  }

  return a >= b ? a : b;
}

/**
 * Folds receipts into a channel's map, keeping the furthest position for each
 * cursor. A snapshot from HTTP and a live `chat.receipt` can arrive in either
 * order; taking the maximum makes the order irrelevant.
 */
export function mergeReceipts(
  existing: Record<string, ChannelReceipt> | undefined,
  incoming: readonly ChannelReceipt[],
): Record<string, ChannelReceipt> {
  const merged: Record<string, ChannelReceipt> = { ...existing };

  for (const receipt of incoming) {
    const previous = merged[receipt.userId];
    merged[receipt.userId] = {
      userId: receipt.userId,
      deliveredUpTo: later(previous?.deliveredUpTo, receipt.deliveredUpTo),
      readUpTo: later(previous?.readUpTo, receipt.readUpTo),
    };
  }

  return merged;
}

export type DeliveryState = 'sent' | 'delivered' | 'seen';

export interface DeliveryStatus {
  state: DeliveryState;
  /** Who has read it - for the "Seen by ..." tooltip in a group. */
  seenBy: string[];
}

/**
 * The tick state of one of the current user's messages.
 *
 *   one tick         sent       - stored; no one else's cursor has reached it
 *   two ticks        delivered  - at least one other member's client has it
 *   two blue ticks   seen       - at least one other member has read it
 *
 * "At least one" in a group, because the question a sender asks is "has anyone
 * seen this?"; `seenBy` carries who, for the ones who want to know.
 */
export function deliveryStatus(
  message: Pick<Message, 'createdAt' | 'senderId'>,
  memberIds: readonly string[],
  receipts: Record<string, ChannelReceipt> | undefined,
): DeliveryStatus {
  const reached = (cursor: string | null | undefined): boolean =>
    cursor !== null && cursor !== undefined && cursor >= message.createdAt;

  const others = memberIds.filter((id) => id !== message.senderId);
  const seenBy = others.filter((id) => reached(receipts?.[id]?.readUpTo));

  if (seenBy.length > 0) {
    return { state: 'seen', seenBy };
  }

  const delivered = others.some((id) => reached(receipts?.[id]?.deliveredUpTo));

  return { state: delivered ? 'delivered' : 'sent', seenBy };
}

/** Stable empty receipts map, for a selector with nothing loaded yet. */
export const EMPTY_RECEIPTS: Record<string, ChannelReceipt> = {};

/**
 * Presence for a user, or `null` when it is genuinely unknown.
 *
 * The server broadcasts presence TRANSITIONS only - there is no snapshot on
 * connect - so a freshly loaded page knows nothing about anyone until they go
 * online or offline. Rendering that absence as "offline" would be a lie, so it
 * is a distinct state in the UI.
 */
export function presenceOf(
  presence: Record<string, PresenceStatus>,
  userId: string,
): PresenceStatus | null {
  return presence[userId] ?? null;
}
